import { EventsPerspective } from '@statsbomb/parachute-types';
import { Nullable } from '@/types/generic';
import { convertFilterParamsToString } from '@/utils/api';

export const playersNormalisedAggregatesUrl = <T>(params: T) =>
  `/players/normalised-aggregates${convertFilterParamsToString(params)}`;

export const playersTotalAggregatesUrl = <T>(params: T) =>
  `/players/total-aggregates${convertFilterParamsToString(params)}`;

export const playersLeagueAverageNormalisedAggregatesUrl = <T>(params: T) =>
  `/players/normalised-aggregates/league-average${convertFilterParamsToString(params)}`;

export const teamsNormalisedAggregatesUrl = <T>(params: T) =>
  `/teams/normalised-aggregates${convertFilterParamsToString(params)}`;

export const teamsLeagueAverageNormalisedAggregatesUrl = <T>(params: T) =>
  `/teams/normalised-aggregates/league-average${convertFilterParamsToString(params)}`;

export const teamsTotalAggregatesUrl = <T>(params: T) =>
  `/teams/total-aggregates${convertFilterParamsToString(params)}`;

export const playerNormalisedAggregatesUrl = <T>(playerId: Nullable<number>, params: T) => {
  if (playerId === null) return undefined;
  return `/player/${playerId}/normalised-aggregates${convertFilterParamsToString(params)}`;
};

export const teamNormalisedAggregatesUrl = <T>(teamId: Nullable<number>, params: T) => {
  if (teamId === null) return undefined;
  return `/team/${teamId}/normalised-aggregates${convertFilterParamsToString(params)}`;
};

export const teamGamesTotalAggregatesUrl = <T>(teamId: Nullable<number>, params: T) => {
  if (teamId === null) return undefined;
  return `/team/${teamId}/games/total-aggregates${convertFilterParamsToString(params)}`;
};

export const playerGamesTotalAggregatesUrl = <T>(playerId: Nullable<number>, params: T) => {
  if (playerId === null) return undefined;
  return `/player/${playerId}/games/total-aggregates${convertFilterParamsToString(params)}`;
};

export const eventsUrl = <T>(params: T) => `/events${convertFilterParamsToString(params)}`;

export const playerMetricEventsUrl = <T>(playerId: Nullable<number>, metricKey: string | undefined, params: T) => {
  if (playerId === null || metricKey === undefined) return undefined;
  return `/player/${playerId}/metric/${metricKey}/events${convertFilterParamsToString(params)}`;
};

export const teamMetricEventsUrl = <T>(teamId: Nullable<number>, metricKey: string | undefined, params: T) => {
  if (teamId === null || metricKey === undefined) return undefined;
  return `/team/${teamId}/metric/${metricKey}/events${convertFilterParamsToString(params)}`;
};

type VisEventsEndpoint = 'events' | 'event-markers' | 'events/count';

type AgainstPerspective = Extract<EventsPerspective, 'against'>;

export type EventsPerspectiveParam = { perspective?: AgainstPerspective };

/**
 * Under Against the team's own events are the opposition's rows, so the scope moves from the team to the opposition.
 * Players have no opposition view.
 */
export const eventScopeKey = (entity: 'player' | 'team', perspective?: AgainstPerspective): string =>
  entity === 'team' && perspective === 'against' ? 'event.opposition_team_id' : `event.${entity}_id`;

/**
 * Lifts `perspective` out of the filter params, so `convertFilterParamsToString` cannot print it a second time.
 */
export const splitPerspective = <T extends EventsPerspectiveParam>({ perspective, ...params }: T) => ({
  perspective,
  params,
});

/**
 * A mapped metric makes the vis page filter-driven: its events come from the global endpoints, scoped to the entity.
 * Otherwise they come from the metric endpoint, which applies the metric's own definition.
 * Under Against, only `event-markers` asks the API to mirror the coordinates.
 */
export const visEventsUrl = <T extends object>(
  entity: 'player' | 'team',
  entityId: Nullable<number>,
  metricKey: string | undefined,
  endpoint: VisEventsEndpoint,
  params: T & { eq?: Record<string, unknown> } & EventsPerspectiveParam,
  isMetricMapped: boolean,
) => {
  if (entityId === null || metricKey === undefined) return undefined;
  const { perspective, params: filterParams } = splitPerspective(params);
  if (!isMetricMapped)
    return `/${entity}/${entityId}/metric/${metricKey}/${endpoint}${convertFilterParamsToString(filterParams)}`;

  // penalty shootouts (period 5) are excluded, as on the metric endpoints and the shot map
  const scopedParams = {
    ...filterParams,
    eq: { ...filterParams.eq, [eventScopeKey(entity, perspective)]: entityId },
    neq: { 'event.period': 5 },
    ...(endpoint === 'event-markers' && entity === 'team' && perspective === 'against' && { perspective }),
  };
  return `/${endpoint}${convertFilterParamsToString(scopedParams)}`;
};

export const metricDistributionPercentilesUrl = <T>(type: 'players' | 'teams', params: T) =>
  `/${type}/metric-distributions/percentiles${convertFilterParamsToString(params)}`;

export const metricDistributionBucketsUrl = <T>(type: 'players' | 'teams', params: T) =>
  `/${type}/metric-distributions/buckets${convertFilterParamsToString(params)}`;
